import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Layers, Plus } from "lucide-react";
import { ProductVariant, ProductAttribute } from "@/types/product-type";
import {
  ProductVariantCreateFormValues,
  ProductVariantUpdateFormValues,
} from "@/types/product-variant-type";
import {
  useCreateProductVariant,
  useUpdateProductVariant,
  useDeleteProductVariant,
} from "@/services/product-variant-service";
import { toast } from "@/hooks/use-toast";
import { useProductWithRelation } from "@/services/product-service";
import { VariantCard } from "./VariantCard";
import { VariantForm } from "./VariantForm";

interface ProductVariantsTabProps {
  productId: string;
  productCode: string;
  productVariants: ProductVariant[];
  productAttributes: ProductAttribute[];
  onAddStock: (variantId: string) => void;
}

export function ProductVariantsTab({
  productId,
  productCode,
  productVariants,
  productAttributes,
  onAddStock,
}: ProductVariantsTabProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState<ProductVariant | null>(
    null
  );
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { refresh } = useProductWithRelation(productId);

  const createMutation = useCreateProductVariant();
  const updateMutation = useUpdateProductVariant();
  const deleteMutation = useDeleteProductVariant();

  const handleCreateSubmit = async (
    data: ProductVariantCreateFormValues | ProductVariantUpdateFormValues
  ) => {
    setIsCreating(true);
    try {
      await createMutation.executeAsync(data as ProductVariantCreateFormValues);
      toast({
        title: "Success",
        description: "Variant created successfully",
      });
      setIsCreateDialogOpen(false);
      refresh();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create variant",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditSubmit = async (
    data: ProductVariantCreateFormValues | ProductVariantUpdateFormValues
  ) => {
    if (!editingVariant) return;

    setIsUpdating(true);
    try {
      await updateMutation.executeAsync({
        id: editingVariant.id,
        data: data as ProductVariantUpdateFormValues,
        productId,
      });
      toast({
        title: "Success",
        description: "Variant updated successfully",
      });
      setIsEditDialogOpen(false);
      setEditingVariant(null);
      refresh();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update variant",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (variant: ProductVariant) => {
    try {
      await deleteMutation.executeAsync({
        id: variant.id,
        productId,
      });
      toast({
        title: "Success",
        description: "Variant deleted successfully",
      });
      refresh();
    } catch (error) {
      console.error("Error deleting variant:", error);
      toast({
        title: "Error",
        description: "Failed to delete variant",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (variant: ProductVariant) => {
    setEditingVariant(variant);
    setIsEditDialogOpen(true);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Product Variants</CardTitle>
            <CardDescription>
              Different variations of this product with stock management
            </CardDescription>
          </div>
          <Dialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
          >
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Variant
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[800px]">
              <DialogHeader>
                <DialogTitle>Create New Variant</DialogTitle>
                <DialogDescription>
                  Add a new variant for this product with different attributes
                  and pricing.
                </DialogDescription>
              </DialogHeader>
              <VariantForm
                mode="create"
                productId={productId}
                productCode={productCode}
                onSubmit={handleCreateSubmit}
                onCancel={() => setIsCreateDialogOpen(false)}
                isLoading={isCreating}
              />
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {productVariants.length > 0 ? (
          <div className="space-y-6">
            {productVariants.map((variant) => (
              <VariantCard
                key={variant.id}
                variant={variant}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAddStock={onAddStock}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Layers className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold">No variants yet</h3>
            <p className="text-muted-foreground mb-4">
              Create variants to offer different options for this product.
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Variant
            </Button>
          </div>
        )}
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Edit Variant</DialogTitle>
            <DialogDescription>
              Update the variant details and pricing.
            </DialogDescription>
          </DialogHeader>
          <VariantForm
            mode="edit"
            productId={productId}
            productCode={productCode}
            editingVariant={editingVariant}
            onSubmit={handleEditSubmit}
            onCancel={() => {
              setIsEditDialogOpen(false);
              setEditingVariant(null);
            }}
            isLoading={isUpdating}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
}
